import React, { useCallback, useEffect, useState } from "react";
import { Checkbox, Flex, Input, Modal, Typography, message } from "antd";
import { CheckCircleOutlined, WarningOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { axiosInstance } from "@/utilities";
import { API_URL_PIM_PRODUCTS } from "@/services/XCONS_ENV";
import { SKU_FABRICANTE_MAX, componerSkuXcons } from "./sku-xcons";

const { Text } = Typography;

interface Props {
  open: boolean;
  onClose: () => void;
  /** El SKU del producto que se duplica. */
  sku: string;
  /**
   * La etiqueta de la marca, si el SKU sigue `MARCA-SKU Fabricante`. Con marca, el
   * duplicado la conserva y se pide sólo el SKU Fabricante nuevo; sin ella, el SKU entero.
   */
  marca: string | null;
}

/**
 * Duplicar un producto (R8): el duplicado nace como borrador. Es la salida para corregir
 * SKU, SKU Fabricante o Marca, que quedan fijos desde el primer guardado.
 *
 * Vive aparte porque se abre desde dos lugares: el ícono del formulario y el menú de
 * acciones del flujo de la ficha (xcons-docs#404).
 */
export const DuplicarProducto: React.FC<Props> = ({
  open,
  onClose,
  sku,
  marca,
}) => {
  const navigate = useNavigate();
  const [skuNuevo, setSkuNuevo] = useState("");
  const [skuLibre, setSkuLibre] = useState<boolean | null>(null);
  const [copiar, setCopiar] = useState({
    categories: true,
    media: true,
    attachments: false,
  });

  // Cada vez que se abre, arranca vacío.
  useEffect(() => {
    if (!open) return;
    setSkuNuevo("");
    setSkuLibre(null);
  }, [open]);

  /** El duplicado conserva la marca, así que su SKU se arma con ella. */
  const duplicaConMarca = !!marca;
  const skuDelDuplicado =
    duplicaConMarca && skuNuevo.trim()
      ? componerSkuXcons(marca!, skuNuevo)
      : skuNuevo.trim();

  /** La unicidad del SKU se chequea en vivo contra las DOS fuentes. */
  const chequearSku = useCallback(async (valor: string) => {
    if (!valor.trim()) {
      setSkuLibre(null);
      return;
    }
    try {
      const { data } = await axiosInstance.get<{ available: boolean }>(
        `${API_URL_PIM_PRODUCTS}/sku-available`,
        { params: { sku: valor.trim() } },
      );
      setSkuLibre(data.available);
    } catch {
      setSkuLibre(null);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => void chequearSku(skuDelDuplicado), 400);
    return () => clearTimeout(t);
  }, [skuDelDuplicado, chequearSku]);

  const duplicar = async () => {
    try {
      const { data } = await axiosInstance.post<{
        id: number;
        omittedCategoryIds?: number[];
      }>(
        `${API_URL_PIM_PRODUCTS}/${encodeURIComponent(sku)}/duplicate`,
        {
          sku: skuDelDuplicado,
          ...(duplicaConMarca ? { skuFabricante: skuNuevo.trim() } : {}),
          ...copiar,
        },
      );
      // Las categorías de tiendas donde no tiene Categorías no se copian: se dice, para que
      // nadie las busque en el duplicado.
      const omitidas = data.omittedCategoryIds?.length ?? 0;
      if (omitidas) {
        message.warning(
          `Duplicado creado como borrador. ${
            omitidas === 1
              ? "1 categoría no se copió"
              : `${omitidas} categorías no se copiaron`
          }: son de tiendas donde no tenés el permiso Categorías.`,
          6,
        );
      } else {
        message.success("Duplicado creado como borrador");
      }
      onClose();
      // El duplicado nace borrador: abre ya editable, para cargar lo que cambia.
      navigate(`/catalog/pending/${data.id}/edit`);
    } catch (e) {
      message.error(
        (e as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? "No se pudo duplicar",
      );
    }
  };

  return (
    <Modal
      open={open}
      title={`Duplicar ${sku}`}
      okText="Duplicar"
      cancelText="Cancelar"
      okButtonProps={{ disabled: !skuLibre }}
      onOk={() => void duplicar()}
      onCancel={onClose}
    >
      <Flex vertical gap={12}>
        <div>
          <Text strong>
            {duplicaConMarca
              ? "SKU Fabricante del duplicado"
              : "SKU del duplicado"}
          </Text>
          <Input
            value={skuNuevo}
            maxLength={duplicaConMarca ? SKU_FABRICANTE_MAX : undefined}
            onChange={(e) => setSkuNuevo(e.target.value)}
            status={skuLibre === false ? "error" : undefined}
            suffix={
              skuLibre === null ? null : skuLibre ? (
                <CheckCircleOutlined style={{ color: "#52c41a" }} />
              ) : (
                <WarningOutlined style={{ color: "#ff4d4f" }} />
              )
            }
          />
          {duplicaConMarca && skuDelDuplicado && (
            <div>
              <Text type="secondary">SKU XCONS: </Text>
              <Text code>{skuDelDuplicado}</Text>
            </div>
          )}
          {skuLibre === false && (
            <Text type="danger">
              Ese SKU ya existe, publicado o como borrador
            </Text>
          )}
        </div>

        <div>
          <Text strong>Qué se copia</Text>
          <Flex vertical>
            <Checkbox checked disabled>
              Atributos (siempre)
            </Checkbox>
            <Checkbox
              checked={copiar.categories}
              onChange={(e) =>
                setCopiar((p) => ({ ...p, categories: e.target.checked }))
              }
            >
              Categorías
            </Checkbox>
            <Checkbox
              checked={copiar.media}
              onChange={(e) =>
                setCopiar((p) => ({ ...p, media: e.target.checked }))
              }
            >
              Imágenes
            </Checkbox>
            <Checkbox
              checked={copiar.attachments}
              onChange={(e) =>
                setCopiar((p) => ({ ...p, attachments: e.target.checked }))
              }
            >
              Fichas técnicas
            </Checkbox>
          </Flex>
        </div>
      </Flex>
    </Modal>
  );
};
